/**
 * MissoesGoalCard — Missões "Meu objetivo" composite card
 *
 * Spec: Figma 6313:24963 (latest). Composite layout = outer beige
 * container (Bege Cararra) wrapping a white inner card and a beige
 * footer band that holds the sobriety line.
 *
 * Structure:
 *   ┌─ outer (#F0EEE4, radius 12, border) ─────────┐
 *   │  ┌─ inner WHITE (radius 12, border, p:24) ─┐ │
 *   │  │ Meu objetivo            >               │ │
 *   │  │ <goal text — Sentient 16 / 140%>        │ │
 *   │  │ ✦ Pergunte ao Bill                      │ │
 *   │  └──────────────────────────────────────────┘ │
 *   │  Você está a 730 dias em sobriedade           │
 *   └──────────────────────────────────────────────┘
 *
 * If `appGoal` is null we fall back to the stage-derived
 * `recoveryStages.<n>.defaultGoal` so the card never reads empty.
 */

import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { Svg, Path } from 'react-native-svg';
import { Ionicons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import { useLanguage } from '@/contexts/LanguageContext';
import type { AppGoalKey } from '@/services/recoveryPlanService';

interface Props {
  stageNumber: 1 | 2 | 3 | 4 | 5;
  appGoal: AppGoalKey | null;
  sobrietyDays: number | null;
  onSobrietyPress?: () => void;
}

const GOAL_TRANSLATION_KEY: Record<AppGoalKey, string> = {
  reduce_control: 'onboarding.goal.options.reduceControl',
  full_abstinence: 'onboarding.goal.options.fullAbstinence',
  maintain_sobriety: 'onboarding.goal.options.maintainSobriety',
  identify_triggers: 'onboarding.goal.options.identifyTriggers',
  healthy_habits: 'onboarding.goal.options.healthyHabits',
  rebuild_relationships: 'onboarding.goal.options.rebuildRelationships',
  monitor_progress: 'onboarding.goal.options.monitorProgress',
};

export default function MissoesGoalCard({ stageNumber, appGoal, sobrietyDays, onSobrietyPress }: Props) {
  const router = useRouter();
  const { t } = useLanguage();

  const goalText = appGoal && GOAL_TRANSLATION_KEY[appGoal]
    ? t(GOAL_TRANSLATION_KEY[appGoal])
    : t(`recoveryStages.${stageNumber}.defaultGoal`);

  const handleAskBill = () => {
    const ctx = appGoal ? `goal:${appGoal}` : `stage:${stageNumber}`;
    const askParam = appGoal ? `&askGoal=${appGoal}` : `&askStage=${stageNumber}`;
    router.push(`/(tabs)?focus=bill&initialContext=${ctx}${askParam}` as any);
  };

  const handleEditGoal = () => {
    router.push('/change-goal' as any);
  };

  const sobrietyIsSet = sobrietyDays !== null && sobrietyDays >= 0;
  const sobrietyText = sobrietyIsSet
    ? t('missoes.goalCard.sobrietyFooter', { days: String(sobrietyDays) })
    : t('missoes.goalCard.setSobriety');

  return (
    <View style={styles.outer}>
      <TouchableOpacity
        style={styles.inner}
        onPress={handleEditGoal}
        activeOpacity={0.85}
        accessibilityRole="button"
        accessibilityLabel={`${t('missoes.goalCard.title')} — ${goalText}`}
      >
        <View style={styles.headerRow}>
          <Text style={styles.title}>{t('missoes.goalCard.title')}</Text>
          <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <Path
              d="M10 8L14 12L10 16"
              stroke="#151515"
              strokeWidth="2"
              strokeLinecap="square"
            />
          </Svg>
        </View>

        <Text style={styles.goal}>{goalText}</Text>

        <TouchableOpacity
          style={styles.askBillRow}
          onPress={handleAskBill}
          activeOpacity={0.7}
          accessibilityRole="button"
          accessibilityLabel={t('missoes.goalCard.askBill')}
        >
          <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <Path
              d="M7.094 3.275c.36-.77 1.453-.77 1.812 0l.293.627a6 6 0 0 0 2.9 2.9l.626.292c.77.36.77 1.453 0 1.812l-.626.293a6 6 0 0 0-2.9 2.9l-.293.626c-.36.77-1.453.77-1.812 0l-.292-.626a6 6 0 0 0-2.9-2.9l-.627-.293c-.77-.36-.77-1.453 0-1.812l.627-.292a6 6 0 0 0 2.9-2.9l.292-.627z"
              fill="#669AB7"
            />
          </Svg>
          <Text style={styles.askBillText}>{t('missoes.goalCard.askBill')}</Text>
        </TouchableOpacity>
      </TouchableOpacity>

      <TouchableOpacity
        style={styles.footer}
        onPress={onSobrietyPress}
        activeOpacity={0.7}
        accessibilityRole="button"
      >
        <Text style={[styles.footerText, !sobrietyIsSet && styles.footerTextCta]}>
          {sobrietyText}
        </Text>
        {!sobrietyIsSet && (
          <Ionicons name="chevron-forward" size={16} color="#7E7D75" />
        )}
      </TouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  // Outer beige (Bege Cararra) shell
  outer: {
    marginHorizontal: 24,
    marginTop: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#F0EEE4',
    backgroundColor: '#F0EEE4',
    overflow: 'hidden',
  },
  // Inner white card
  inner: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#F0EEE4',
    padding: 24,
    gap: 8,
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },
  title: {
    color: '#151515',
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    fontWeight: '700',
    lineHeight: 19.6,
  },
  goal: {
    color: '#000000',
    fontFamily: 'Sentient-Regular',
    fontSize: 16,
    fontWeight: '400',
    lineHeight: 22.4,
  },
  askBillRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingTop: 4,
  },
  askBillText: {
    color: '#669AB7',
    fontFamily: 'Inter_700Bold',
    fontSize: 12,
    fontWeight: '700',
    lineHeight: 12,
  },
  // Beige footer band (height 44, p:16/24)
  footer: {
    height: 44,
    paddingHorizontal: 24,
    paddingVertical: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  footerText: {
    color: '#7E7D75',
    fontFamily: 'Inter_700Bold',
    fontSize: 12,
    fontWeight: '700',
    lineHeight: 12,
  },
  footerTextCta: {
    color: '#7E7D75',
  },
});
